import { useMemo } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'

import MarkDown from '~/components/Helpers/MarkDown'
import Icon from '~/components/Helpers/Icon'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import { ContactLinkProps } from '~/components/UI/Buttons/ContactLink/ContactLink'

import Whatsapp from '~/assets/icons/whatsapp'
import LinkedIn from '~/assets/icons/linkedin'
import { CardProps } from '~/components/types/card'

const cardVariants = cva(
  'relative flex max-h-[500px] !h-[500px] flex-col p-xs-plus sm:p-s lg:p-m gap-s',
  {
    variants: {
      backgroundColour: {
        'ink-blue': 'bg-blue-d text-white',
        'mid-blue': 'bg-blue-m text-white',
        chartreuse: 'bg-char text-blue-d',
        'light-grey': 'bg-grey-l text-blue-d',
        white: 'bg-white text-blue-d'
      }
    },
    defaultVariants: {
      backgroundColour: 'ink-blue'
    }
  }
)

export type SingleContactCardProps = CardProps & {
  content: {
    contactLinks: ContactLinkProps['data']
  }
}

const SingleContactCard = ({
  content,
  carousel,
  className
}: SingleContactCardProps) => {
  const { backgroundColour, heading, copy, contactLinks } = content

  const foregroundColour =
    backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
      ? 'white'
      : 'ink-blue'

  const linkType = useMemo(() => {
    switch (content?.contactLinks?.type) {
      case 'phone':
        return {
          icon: 'mobile',
          link: `tel:${contactLinks?.link.replace(/\s+|\(.*?\)/g, '')}`
        }
      case 'email':
        return {
          icon: 'envelope',
          link: `mailto:${contactLinks?.link}`
        }
      case 'text':
        return {
          icon: 'messages',
          link: `sms:${contactLinks?.link}`
        }
      case 'live-chat':
        return {
          icon: 'messages',
          link: contactLinks?.link
        }
      case 'enquiry-form':
        return {
          icon: 'file-edit',
          link: contactLinks?.link
        }
      case 'callback':
        return {
          icon: 'send-to-phone',
          link: contactLinks?.link
        }
      case 'whatsapp':
        return {
          icon: (
            <Whatsapp
              fill={foregroundColour}
              secondaryfill={foregroundColour === 'ink-blue' ? '#1d4cf2' : ''}
              width={'4em'}
              height={'4em'}
              title={'WhatsApp'}
            />
          ),
          link: `https://wa.me/${contactLinks?.link.replace(/\s+|\(.*?\)/g, '')}`
        }
      case 'linkedin':
        return {
          icon: (
            <LinkedIn
              fill={foregroundColour}
              width={'4em'}
              height={'4em'}
              title={'LinkedIn'}
            />
          ),
          link: contactLinks.link
        }
      case 'international':
        return {
          icon: 'world',
          link: `tel:${contactLinks?.link.replace(/\s+|\(.*?\)/g, '')}`
        }

      default:
        return {
          icon: 'mobile',
          link: contactLinks?.link
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactLinks?.type])

  return (
    <div className={cn(cardVariants({ backgroundColour }), className)}>
      <div className='flex grow'>
        {contactLinks?.type !== 'whatsapp' &&
          contactLinks?.type !== 'linkedin' && (
            <Icon
              icon={{
                image: {
                  altText: undefined,
                  asset: {
                    sys: {
                      uri: `/site-elements/icons/single-colour/ink-blue/${linkType.icon}.svg`
                    }
                  }
                },
                duotone: true
              }}
              backgroundColour={backgroundColour}
              className={cn(
                'self-end',
                carousel && !content.featured ? '[&>img]:h-l' : '[&>img]:h-xl'
              )}
            />
          )}
        {(contactLinks?.type === 'whatsapp' ||
          contactLinks?.type === 'linkedin') && (
          <div className='self-end'>{linkType.icon}</div>
        )}
      </div>
      <h4>{heading}</h4>
      <div className='w-fit'>
        <TertiaryLink
          data={{
            externalLink: null,
            internalLink: {
              sys: { uri: linkType.link }
            },
            ariaLabel: contactLinks?.ariaLabel,
            newWindow: false,
            label: contactLinks?.label,
            strongColour: true
          }}
          colour={foregroundColour}
        />
      </div>
      <MarkDown className='flex items-center' content={copy} />
    </div>
  )
}

export default SingleContactCard
